import type {CreateExtArgs, DalItem, DalValue, DmApis, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {
    BuilderTypes,
    CompRef,
    BuilderStyle,
    Pointer,
    InnerElementPath,
    BuilderPresets,
    Rect,
    CompStructure,
    SerializedCompStructure,
    PossibleStyleForBuilderComponent
} from '@wix/document-services-types'
import type {VariantsExtensionAPI} from '../variants/variants'
import {stripHashIfExists} from '../../utils/refArrayUtils'
import type {SchemaExtensionAPI} from '../schema/schema'
import {ReportableError} from '@wix/document-manager-utils'
import {DATA_TYPES, COMP_DATA_QUERY_KEYS_WITH_STYLE} from '../../constants/constants'
import isNil from 'lodash/isNil'
import _ from 'lodash'
import type {DataExtensionAPI} from '../data'
import {validateLanguageCode} from '../../utils/builderUtils'
import {EVENTS, type SlotsExtensionAPI} from '../slots'
import type {ComponentsExtensionAPI} from '../components/components'
import type {ViewerExtensionAPI} from '../viewer/viewerExtension'
import {normalizeCompRefVariants} from '../../utils/variantsUtils'
import type {CssStyleExtensionAPI} from '../cssStyle/cssStyle'
import {removeDataByNamespace} from '../../utils/dataUtils'
import type {StylesExtensionApi} from '../style/style'
import {DATA_MODEL_HOOKS} from '../dataModel/hooks'
import type {HooksExtensionApi} from '../hooks/hooks'

interface InnerElementsApi extends ExtensionAPI {
    getInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): DalItem | undefined
    getOrCreateInnerElement(compRef: CompRef, innerElementPath: InnerElementPath, pageId: string): DalItem
    getInnerElementData(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode?: string
    ): DalItem | undefined
    getInnerElementTranslationOverrides(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode: string
    ): DalItem | undefined
    setInnerElementData(compRef: CompRef, innerElementPath: InnerElementPath, data: any, languageCode?: string): void
    removeInnerElementTranslationOverrides(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode: string
    ): void
    removeInnerElementTranslationOverridesForAField(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        languageCode: string,
        field: string
    ): void
    getInnerElementStyle(compRef: CompRef, innerElementPath: InnerElementPath): PossibleStyleForBuilderComponent
    setInnerElementStyle(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        style: Partial<BuilderStyle>,
        shouldRemoveRelatedProperties?: boolean
    ): void
    connectToGlobalStyle(
        componentPointer: CompRef,
        innerElementPath: InnerElementPath,
        globalStylePointerOrId: Pointer | string
    ): void
    removeInnerElementStyleProperties(compRef: CompRef, innerElementPath: InnerElementPath, paths: string[]): void
    getInnerElementPresets(compRef: CompRef, innerElementPath: InnerElementPath): BuilderPresets | undefined
    setInnerElementPresets(compRef: CompRef, innerElementPath: InnerElementPath, presets: BuilderPresets): void
    serializeInnerElementPath(innerElementPath: InnerElementPath): string
    setInnerElementSlots(
        componentToAddPointer: CompRef,
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        slotName: string,
        componentDefinition: CompStructure
    ): Pointer
    getInnerElementSlots(compRef: CompRef, innerElementPath: InnerElementPath): DalValue | void
    getInnerElementMeasurements(innerElementPath: InnerElementPath): Rect
    removeInnerElementDataProperties(
        compRef: CompRef,
        innerElementPath: InnerElementPath,
        propsToRemove: string[]
    ): void
}

interface InnerElementsExtensionAPI extends ExtensionAPI {
    innerElements: InnerElementsApi
}

function throwError(message: string, extras: any = {}) {
    throw new ReportableError({errorType: 'InnerElementsError', message, ...extras})
}

const createExtension = (): Extension => {
    const createExtensionAPI = (args: CreateExtArgs): InnerElementsExtensionAPI => {
        const {dal, pointers, eventEmitter} = args
        const {
            schemaAPI,
            dataModel,
            viewer,
            variants,
            data: dataExtension,
            slots,
            components,
            cssStyle,
            style,
            hooks
        } = args.extensionAPI as SchemaExtensionAPI &
            DataModelExtensionAPI &
            ViewerExtensionAPI &
            DataExtensionAPI &
            VariantsExtensionAPI &
            SlotsExtensionAPI &
            ComponentsExtensionAPI &
            CssStyleExtensionAPI &
            StylesExtensionApi &
            HooksExtensionApi

        const namespace = DATA_TYPES.innerElements

        function innerElementsPointer(id: string, overrides?: Partial<Pointer>): Pointer {
            return pointers.getPointer(id, DATA_TYPES.innerElements, overrides)
        }

        function getComp(compRef: CompRef): DalItem {
            const comp = dal.get(compRef)
            if (!comp) {
                throwError('Component not found', {compRef})
            }
            return comp
        }

        function getOrCreateInnerElementsMapPointerFromComp(compRef: CompRef): Pointer {
            const comp = getComp(compRef)
            let innerElementsQuery = stripHashIfExists(comp.innerElementsQuery)
            if (!innerElementsQuery) {
                const mapType = builderTypes(comp.componentType).innerElementsMap
                const mapPointer = dataModel.components.addItem(compRef, namespace, {
                    type: mapType,
                    metaData: {
                        pageId: compRef.pageId
                    }
                })
                innerElementsQuery = mapPointer.id
            }
            return innerElementsPointer(innerElementsQuery)
        }

        function getOrCreateInnerElementsMapPointerFromInnerElement(innerElementId: string): Pointer {
            const pointer = innerElementsPointer(innerElementId)
            const innerElement = dal.get(pointer)
            if (!innerElement) {
                throwError('Inner element not found', {innerElementId})
            }
            if (!innerElement.innerElementsQuery) {
                const mapPointer = dataModel.addItem(
                    {
                        type: builderTypes(innerElement.referredType).innerElementsMap
                    },
                    namespace,
                    innerElement.metaData.pageId!
                )
                dataModel.linkDataToItemByType(
                    pointer,
                    mapPointer.id,
                    DATA_TYPES.innerElements,
                    COMP_DATA_QUERY_KEYS_WITH_STYLE.innerElements
                )
            }
            const updatedElement = dal.get(pointer)
            return innerElementsPointer(stripHashIfExists(updatedElement.innerElementsQuery!))
        }

        function getOrCreateInnerElementFromComponent(
            compRef: CompRef,
            innerElementName: string,
            pageId: string
        ): DalItem {
            const comp = getComp(compRef)
            const compType = comp.componentType
            const innerType = getReferredTypeForNestedElement(compType, innerElementName)

            const mapPointer = getOrCreateInnerElementsMapPointerFromComp(compRef)
            const map = dal.get(mapPointer)
            if (!map[DATA_TYPES.innerElements]?.[innerElementName]) {
                const ptr = dataModel.addItem(
                    {
                        type: 'InnerElement',
                        referredType: innerType
                    },
                    namespace,
                    pageId
                )
                const innerMapPointer = pointers.getInnerPointer(mapPointer, [
                    DATA_TYPES.innerElements,
                    innerElementName
                ])
                dal.set(innerMapPointer, ptr.id)
            }

            // Possible micro-optimization: No need for dal accesses here, we already have the info in scope
            const updatedMap = dal.get(mapPointer)
            const innerElementId = stripHashIfExists(updatedMap[DATA_TYPES.innerElements][innerElementName])
            return dal.get(innerElementsPointer(innerElementId))
        }

        function getOrCreateInnerElementFromInnerElement(pageId: string, source: DalItem, targetName: string): DalItem {
            const mapPointer = getOrCreateInnerElementsMapPointerFromInnerElement(source.id!)
            const map = dal.get(mapPointer)
            const targetReferredType = getReferredTypeForNestedElement(source.referredType, targetName)
            if (!map[DATA_TYPES.innerElements]?.[targetName]) {
                const ptr = dataModel.addItem(
                    {
                        type: 'InnerElement',
                        referredType: targetReferredType
                    },
                    namespace,
                    pageId
                )
                const innerMapPointer = pointers.getInnerPointer(mapPointer, [DATA_TYPES.innerElements, targetName])
                dal.set(innerMapPointer, ptr.id)
            }
            const updatedMap = dal.get(mapPointer)
            return dal.get(innerElementsPointer(stripHashIfExists(updatedMap[DATA_TYPES.innerElements][targetName])))
        }

        function getOrCreateInnerElement(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            pageId: string
        ): DalItem {
            const [firstName, ...restOfNames] = innerElementPath
            const firstElement = getOrCreateInnerElementFromComponent(compRef, firstName, pageId)
            return restOfNames.reduce(
                (acc, name) => getOrCreateInnerElementFromInnerElement(pageId, acc, name),
                firstElement
            )
        }

        function getInnerElementFromComponent(compRef: CompRef, innerElementName: string): DalItem | undefined {
            const comp = getComp(compRef)
            const innerElementsQuery = stripHashIfExists(comp.innerElementsQuery)
            if (!innerElementsQuery) {
                return undefined
            }
            const map = dal.get(innerElementsPointer(innerElementsQuery))
            if (!map) {
                return undefined
            }
            const innerElementId = stripHashIfExists(map[DATA_TYPES.innerElements][innerElementName])
            if (!innerElementId) {
                return undefined
            }
            return dal.get(innerElementsPointer(innerElementId))
        }

        function getInnerElementFromInnerElement(source: DalItem, innerElementName: string): DalItem | undefined {
            const innerElementsQuery = stripHashIfExists(source.innerElementsQuery)
            if (!innerElementsQuery) {
                return undefined
            }
            const map = dal.get(innerElementsPointer(innerElementsQuery))
            if (!map) {
                return undefined
            }
            const innerElementId = stripHashIfExists(map[DATA_TYPES.innerElements][innerElementName])
            if (!innerElementId) {
                return undefined
            }
            return dal.get(innerElementsPointer(innerElementId))
        }

        function getInnerElement(compRef: CompRef, innerElementPath: InnerElementPath): DalItem | undefined {
            const [firstName, ...restOfNames] = innerElementPath
            const firstElement = getInnerElementFromComponent(compRef, firstName)
            return restOfNames.reduce(
                (acc, name) => (acc ? getInnerElementFromInnerElement(acc, name) : undefined),
                firstElement
            )
        }

        function builderTypes(compType: string): BuilderTypes {
            return (
                schemaAPI.getDefinition(compType)?.builderTypes ??
                throwError(`No builderTypes for component type ${compType}`, {compType})
            )
        }

        function getReferredTypeForNestedElement(sourceType: string, innerElementName: string): string {
            const sourceBuilderTypes = builderTypes(sourceType)
            return (
                sourceBuilderTypes.innerElementTypes[innerElementName] ??
                throwError('Failed to get type info for innerElementType', {innerElementType: sourceType})
            )
        }

        function getInnerElementMeasurements(innerElementPath: InnerElementPath): Rect {
            const componentBoundingBoxArr = viewer?.getInnerElementBoundingBoxes(innerElementPath) ?? []
            let left = Infinity
            let right = -Infinity
            let top = Infinity
            let bottom = -Infinity
            if (_.isEmpty(componentBoundingBoxArr)) return {x: 0, y: 0, height: 9, width: 16}
            for (const boundingBox of componentBoundingBoxArr) {
                if (boundingBox.absoluteLeft < left) left = boundingBox.absoluteLeft
                if (boundingBox.absoluteLeft > right) right = boundingBox.absoluteLeft
                if (boundingBox.absoluteTop < top) top = boundingBox.absoluteTop
                if (boundingBox.absoluteTop > bottom) bottom = boundingBox.absoluteTop
            }
            return {
                x: left,
                y: top,
                height: bottom - top,
                width: right - left
            }
        }

        function getInnerElementData(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode?: string
        ): DalItem | undefined {
            const innerElement = getInnerElement(compRef, innerElementPath)
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const dataId = stripHashIfExists(innerElement?.dataQuery)
            let dataItem = dataExtension.getInLanguage(pageId, dataId, languageCode)
            dataItem = dataItem
                ? hooks.executeHookAndUpdateValue(
                      DATA_MODEL_HOOKS.DATA.AFTER_GET_PUBLIC.createEvent({
                          componentPointer: compRef,
                          dataItem
                      }),
                      dataItem
                  )
                : dataItem

            return dataItem
        }

        function getInnerElementTranslationOverrides(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode: string
        ): DalItem | undefined {
            validateLanguageCode(languageCode)

            const innerElement = getInnerElement(compRef, innerElementPath)
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const dataId = stripHashIfExists(innerElement?.dataQuery)

            return dataModel.getItem(dataId, DATA_TYPES.data, pageId, languageCode)
        }

        function setInnerElementData(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            rawData: any,
            languageCode?: string
        ): void {
            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)

            const expectedDataType = getExpectedDataType(compRef, innerElementPath)
            let dataItem = autoType(rawData, expectedDataType)
            const dataId = stripHashIfExists(innerElement.dataQuery)

            dataItem = hooks.executeHookAndUpdateValue(
                DATA_MODEL_HOOKS.DATA.BEFORE_UPDATE_PUBLIC.createEvent({
                    componentPointer: innerElementsPointer(innerElement.id!),
                    dataItem
                }),
                dataItem
            )

            //todo: the hook above is going to be removed
            hooks.executeHook(
                DATA_MODEL_HOOKS.DATA.INNER_ELEMENT_UPDATE_BEFORE.createEvent({
                    componentPointer: compRef,
                    dataItem
                })
            )

            dataExtension.updateInLanguage(
                innerElementsPointer(innerElement.id!),
                dataItem,
                pageId,
                dataId,
                languageCode
            )
        }

        function setInnerElementSlots(
            componentToAddPointer: CompRef,
            slottedComponent: CompRef,
            innerElementPath: InnerElementPath,
            slotName: string,
            componentDefinition: SerializedCompStructure
        ) {
            const ownerOfComponentInSlotPointer = slots.getOwnerOfComponentInSlot(slottedComponent)
            const addedComponentPointer = components.addComponentV2(
                componentToAddPointer,
                slottedComponent,
                componentDefinition
            )
            const pageId = pointers.structure.getPageOfComponent(slottedComponent).id
            const innerElement = getOrCreateInnerElement(slottedComponent, innerElementPath, pageId)
            const slotsDataType = getExpectedSlotsType(slottedComponent, innerElementPath)
            const updatedData = slots.getSlotsDataItem(innerElementsPointer(innerElement.id!) as CompRef)
            if (updatedData?.slots) {
                updatedData.slots[slotName] = addedComponentPointer.id
                return dataModel.components.addItem(
                    innerElementsPointer(innerElement.id!),
                    DATA_TYPES.slots,
                    updatedData
                )
            }
            dataModel.components.addItem(innerElementsPointer(innerElement.id!), DATA_TYPES.slots, {
                type: slotsDataType,
                slots: {[slotName]: addedComponentPointer.id}
            })

            eventEmitter.emit(
                EVENTS.SLOTS.AFTER_POPULATE,
                slottedComponent,
                ownerOfComponentInSlotPointer,
                addedComponentPointer
            )

            return addedComponentPointer
        }

        function getInnerElementSlots(compRef: CompRef, innerElementPath: InnerElementPath): DalItem | void {
            const comp = dal.get(compRef)
            if (!comp) {
                throwError('Component not found', {compRef})
            }

            const innerElement = getInnerElement(compRef, innerElementPath)
            const innerElementPointer = innerElementsPointer(innerElement!.id!)
            return slots.getSlotsDataItem(innerElementPointer as CompRef)
        }

        function removeInnerElementTranslationOverrides(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode: string
        ): void {
            validateLanguageCode(languageCode)

            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const innerElement = getInnerElement(compRef, innerElementPath)
            const dataId = stripHashIfExists(innerElement?.dataQuery)
            const translationOverridesPointer = pointers.multilingualTranslations.translationDataItem(
                pageId,
                languageCode,
                dataId
            )
            dataModel.removeItemRecursively(translationOverridesPointer)
        }

        function removeInnerElementTranslationOverridesForAField(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            languageCode: string,
            field: string
        ) {
            validateLanguageCode(languageCode)

            const translationOverridesDataItem = getInnerElementTranslationOverrides(
                compRef,
                innerElementPath,
                languageCode
            )
            if (translationOverridesDataItem) {
                delete translationOverridesDataItem.data[field]
                if (_.isEmpty(translationOverridesDataItem.data)) {
                    removeInnerElementTranslationOverrides(compRef, innerElementPath, languageCode)
                } else {
                    setInnerElementData(compRef, innerElementPath, translationOverridesDataItem, languageCode)
                }
            }
        }

        function recurseBuilderTypes(compRef: CompRef, innerElementPath: InnerElementPath): BuilderTypes {
            const comp = getComp(compRef)
            const compBuilderTypes = builderTypes(comp.componentType)
            if (innerElementPath.length < 1) {
                return compBuilderTypes
            }
            const names = innerElementPath
            return names.reduce((acc, name) => {
                const childType = acc.innerElementTypes[name]
                return builderTypes(childType)
            }, compBuilderTypes)
        }

        function getExpectedDataType(compRef: CompRef, innerElementPath: InnerElementPath): string {
            return recurseBuilderTypes(compRef, innerElementPath).data
        }

        function getExpectedStyleType(compRef: CompRef, innerElementPath: InnerElementPath): BuilderTypes['style'] {
            return recurseBuilderTypes(compRef, innerElementPath).style
        }

        function getExpectedPresetsType(compRef: CompRef, innerElementPath: InnerElementPath): string {
            return recurseBuilderTypes(compRef, innerElementPath).presets
        }

        function getExpectedSlotsType(compRef: CompRef, innerElementPath: InnerElementPath): string {
            return recurseBuilderTypes(compRef, innerElementPath).slots
        }

        function autoType<T extends Record<string, any>>(obj: T, expectedType: string): T {
            if (isNil(obj)) {
                return obj
            }
            const typeWasExplicitlyDeclared = obj.type !== undefined
            if (typeWasExplicitlyDeclared && obj.type !== expectedType) {
                throwError('Type mismatch', {expectedType, actualType: obj.type})
            }
            return typeWasExplicitlyDeclared ? obj : {...obj, type: expectedType}
        }

        const getEmptyInnerElementStyle = (compRef: CompRef, innerElementPath: InnerElementPath): BuilderStyle => {
            const styleType = getExpectedStyleType(compRef, innerElementPath)
            return {
                type: styleType
            }
        }

        const getInnerElementStyle = (
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): PossibleStyleForBuilderComponent => {
            const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)

            if (!dal.has(_.omit(compRef, ['variants']))) {
                throwError('Component not found', {compRef: compRefWithNormalizedVariants})
            }

            const innerElement = getInnerElement(compRefWithNormalizedVariants, innerElementPath)
            if (!innerElement) {
                return
            }
            return cssStyle.components.getCSSStyleByPointer(
                innerElementsPointer(innerElement.id!, {
                    variants: compRefWithNormalizedVariants.variants
                })
            )
        }

        const getResolvedInnerElementStyle = (
            compRef: CompRef,
            innerElementPath: InnerElementPath
        ): BuilderStyle | undefined => {
            let styleItem = getInnerElementStyle(compRef, innerElementPath)

            if (styleItem?.type === 'GlobalStyle') {
                const globalStylePointer = style.global.getPointer(styleItem.id)
                styleItem = style.global.get(globalStylePointer) as BuilderStyle
            }

            return styleItem
        }

        function removeInnerElementDataProperties(
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            propsToRemove: string[]
        ): void {
            const innerElement = getInnerElement(compRef, innerElementPath)
            const innerElementPointer = innerElementsPointer(
                innerElement!.id!,
                compRef.variants ? {variants: compRef.variants} : {}
            )

            removeDataByNamespace(args.extensionAPI, innerElementPointer, DATA_TYPES.data, propsToRemove, true)
        }

        const connectToGlobalStyle = (
            compRef: CompRef,
            innerElementPath: InnerElementPath,
            globalStylePointerOrId: Pointer | string
        ): void => {
            const globalStyleDefinition = style.global.definition.get(globalStylePointerOrId)
            const expectedStyleType = getExpectedStyleType(compRef, innerElementPath)
            if (globalStyleDefinition.styleType !== expectedStyleType) {
                throw new Error(
                    `The style type ${globalStyleDefinition.styleType} is not derived from the component manifest. The inner element style type is ${expectedStyleType}`
                )
            }

            const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
            const pageId = pointers.structure.getPageOfComponent(compRefWithNormalizedVariants).id
            const innerElement = getOrCreateInnerElement(compRefWithNormalizedVariants, innerElementPath, pageId)
            const innerElementPointer = innerElementsPointer(innerElement.id!, {
                variants: compRefWithNormalizedVariants.variants
            })

            style.connectToGlobalStyle(innerElementPointer, globalStylePointerOrId)
        }

        return {
            innerElements: {
                getInnerElementMeasurements,
                getInnerElement,
                getOrCreateInnerElement,
                serializeInnerElementPath(innerElementPath: InnerElementPath) {
                    return innerElementPath.join('|')
                },
                getInnerElementData,
                getInnerElementTranslationOverrides,
                setInnerElementData,
                removeInnerElementTranslationOverrides,
                removeInnerElementTranslationOverridesForAField,
                getInnerElementStyle,
                setInnerElementStyle(
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    partialStyle: Partial<BuilderStyle>,
                    shouldRemoveRelatedProperties?: boolean
                ) {
                    const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
                    const pageId = pointers.structure.getPageOfComponent(compRefWithNormalizedVariants).id
                    const innerElement = getOrCreateInnerElement(
                        compRefWithNormalizedVariants,
                        innerElementPath,
                        pageId
                    )
                    const existingStyle =
                        getResolvedInnerElementStyle(compRefWithNormalizedVariants, innerElementPath) ??
                        getEmptyInnerElementStyle(compRefWithNormalizedVariants, innerElementPath)

                    const valueToUpdate = cssStyle.getUpdatedCSSStyleItemForUpdate(
                        existingStyle,
                        partialStyle,
                        shouldRemoveRelatedProperties
                    )
                    variants.updateDataConsideringVariants({
                        pointerWithVariants: innerElementsPointer(innerElement.id!, {
                            variants: compRefWithNormalizedVariants.variants
                        }),
                        valueToUpdate,
                        namespace: DATA_TYPES.theme,
                        referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.style
                    })
                },
                connectToGlobalStyle,
                removeInnerElementStyleProperties(
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    paths: string[]
                ) {
                    const compRefWithNormalizedVariants = normalizeCompRefVariants(compRef)
                    const innerElement = getInnerElement(compRefWithNormalizedVariants, innerElementPath)
                    if (!innerElement?.styleId) {
                        return
                    }

                    const styleItem = getResolvedInnerElementStyle(compRefWithNormalizedVariants, innerElementPath)
                    if (styleItem) {
                        const valueToUpdate = cssStyle.getUpdatedCSSStyleItemForRemove(styleItem, paths)
                        if (valueToUpdate) {
                            variants.updateDataConsideringVariants({
                                pointerWithVariants: innerElementsPointer(innerElement.id!, {
                                    variants: compRefWithNormalizedVariants.variants
                                }),
                                valueToUpdate,
                                namespace: DATA_TYPES.theme,
                                referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.style
                            })
                        }
                    }
                },
                getInnerElementPresets(compRef: CompRef, innerElementPath: InnerElementPath) {
                    const comp = dal.get(compRef)
                    if (!comp) {
                        throwError('Component not found', {compRef})
                    }

                    const innerElement = getInnerElement(compRef, innerElementPath)
                    if (!innerElement?.presetsQuery) {
                        return undefined
                    }

                    return variants.getComponentItemConsideringVariants(
                        innerElementsPointer(innerElement.id!, {variants: compRef.variants}),
                        DATA_TYPES.presets
                    )
                },
                setInnerElementPresets(
                    compRef: CompRef,
                    innerElementPath: InnerElementPath,
                    presets: BuilderPresets
                ): void {
                    const pageId = pointers.structure.getPageOfComponent(compRef).id
                    const innerElement = getOrCreateInnerElement(compRef, innerElementPath, pageId)
                    const expectedPresetsType = getExpectedPresetsType(compRef, innerElementPath)
                    variants.updateDataConsideringVariants({
                        pointerWithVariants: innerElementsPointer(innerElement.id!, {
                            variants: compRef.variants
                        }),
                        valueToUpdate: autoType(presets, expectedPresetsType),
                        namespace: DATA_TYPES.presets,
                        referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.presets
                    })
                },
                setInnerElementSlots,
                getInnerElementSlots,
                removeInnerElementDataProperties
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {innerElements} = extensionAPI as InnerElementsExtensionAPI
        return {
            innerElements: {
                getInnerElementTranslationOverrides: innerElements.getInnerElementTranslationOverrides,
                removeInnerElementTranslationOverrides: innerElements.removeInnerElementTranslationOverrides,
                removeInnerElementTranslationOverridesForAField:
                    innerElements.removeInnerElementTranslationOverridesForAField,
                removeInnerElementDataProperties: innerElements.removeInnerElementDataProperties,
                serializeInnerElementPath: innerElements.serializeInnerElementPath,
                getInnerElementData: innerElements.getInnerElementData,
                setInnerElementData: innerElements.setInnerElementData,
                getInnerElementStyle: innerElements.getInnerElementStyle,
                setInnerElementStyle: innerElements.setInnerElementStyle,
                connectToGlobalStyle: innerElements.connectToGlobalStyle,
                removeInnerElementStyleProperties: innerElements.removeInnerElementStyleProperties,
                getInnerElementPresets: innerElements.getInnerElementPresets,
                setInnerElementPresets: innerElements.setInnerElementPresets,
                setInnerElementSlots: innerElements.setInnerElementSlots,
                getInnerElementSlots: innerElements.getInnerElementSlots
            }
        }
    }

    return {
        name: 'innerElements',
        dependencies: new Set(['schema', 'data', 'dataModel', 'slots', 'variants', 'viewer', 'cssStyle']),
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
export type {InnerElementsExtensionAPI, InnerElementsApi, InnerElementPath}
